import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

if (!GOOGLE_CLIENT_ID) {
  console.error('REACT_APP_GOOGLE_CLIENT_ID environment variable is required');
}

function GoogleSignIn({ text = 'Continue with Google', disabled = false }) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef(null);
  const isInitialized = useRef(false);
  const loadingTimeoutRef = useRef(null);

  const handleCredentialResponse = useCallback(async (response) => {
    // Clear the timeout since we got a response
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    
    if (!response || !response.credential) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await loginWithGoogle(response.credential);
      if (result.success) {
        navigate('/');
      } else {
        console.error('Google sign-in failed:', result.error);
        alert(result.error || 'Google sign-in failed');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      console.error('Error details:', error.message, error.stack);
      alert(`An error occurred during Google sign-in: ${error.message || 'Please try again.'}`);
      setIsLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    // Check if client ID is available
    if (!GOOGLE_CLIENT_ID) {
      console.error('Google Client ID is not set. Please check REACT_APP_GOOGLE_CLIENT_ID environment variable.');
      return;
    }

    // Initialize Google Sign-In when library loads
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts && window.google.accounts.id && !isInitialized.current) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            // Disable One Tap completely - we only want button-based sign-in with account selection
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          // Render Google's button in a hidden container
          if (buttonRef.current) {
            try {
              window.google.accounts.id.renderButton(
                buttonRef.current,
                {
                  type: 'standard',
                  theme: 'outline',
                  size: 'large',
                  text: 'signin_with',
                  width: '100%',
                }
              );
            } catch (error) {
              console.error('Error rendering Google button:', error);
            }
          }

          isInitialized.current = true;
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
        }
      }
    };

    // Function to load Google script if not already loaded
    const loadGoogleScript = () => {
      // Check if script is already in the DOM
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        // Script exists, just wait for it to load
        if (existingScript.onload) {
          // Already has onload handler, just check if loaded
          if (window.google && window.google.accounts && window.google.accounts.id) {
            initializeGoogleSignIn();
          }
        } else {
          existingScript.onload = () => {
            if (window.google && window.google.accounts && window.google.accounts.id) {
              initializeGoogleSignIn();
            }
          };
        }
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait a bit for the library to fully initialize
        setTimeout(() => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            initializeGoogleSignIn();
          } else {
            console.error('Google Sign-In library loaded but API not available');
          }
        }, 100);
      };
      script.onerror = () => {
        console.error('Failed to load Google Sign-In script');
      };
      document.head.appendChild(script);
    };

    // Try to initialize immediately
    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGoogleSignIn();
    } else {
      // Check if script is loading or load it
      loadGoogleScript();
      
      // Also set up a polling check as backup
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds total (50 * 100ms)
      const checkGoogle = setInterval(() => {
        attempts++;
        if (window.google && window.google.accounts && window.google.accounts.id) {
          initializeGoogleSignIn();
          clearInterval(checkGoogle);
        } else if (attempts >= maxAttempts) {
          console.error('Google Sign-In library failed to load after 5 seconds');
          clearInterval(checkGoogle);
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
  }, [handleCredentialResponse]);

  const handleClick = (e) => {
    e.preventDefault();
    
    if (!GOOGLE_CLIENT_ID) {
      alert('Google Sign-In is not configured. Please contact support.');
      return;
    }
    
    if (!isInitialized.current || !window.google || !window.google.accounts || !window.google.accounts.id) {
      console.error('Google Sign-In not initialized:', {
        isInitialized: isInitialized.current,
        hasGoogle: !!window.google,
        hasAccounts: !!(window.google && window.google.accounts),
        hasId: !!(window.google && window.google.accounts && window.google.accounts.id)
      });
      alert('Google Sign-In is not ready. Please refresh the page.');
      return;
    }

    // Find and click the actual Google button which will open account selection popup
    if (buttonRef.current) {
      // Look for the Google button element
      const googleButton = buttonRef.current.querySelector('div[role="button"]');
      
      if (googleButton) {
        setIsLoading(true);
        // Clear any existing timeout
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        // Click the Google button - this opens the popup with account selection
        googleButton.click();
        
        // Reset loading if popup is closed without selecting (after 30 seconds timeout)
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(false);
        }, 30000);
      } else {
        // Button not rendered yet, try to render it manually
        console.warn('Google button element not found, attempting to render manually');
        try {
          if (buttonRef.current && window.google.accounts.id) {
            window.google.accounts.id.renderButton(
              buttonRef.current,
              {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                width: '100%',
              }
            );
            // Wait a bit for button to render, then try clicking
            setTimeout(() => {
              const retryButton = buttonRef.current?.querySelector('div[role="button"]');
              if (retryButton) {
                setIsLoading(true);
                retryButton.click();
                loadingTimeoutRef.current = setTimeout(() => {
                  setIsLoading(false);
                }, 30000);
              } else {
                setIsLoading(false);
                console.error('Google button still not found after manual render');
                alert('Unable to initialize Google Sign-In. Please refresh the page.');
              }
            }, 300);
          } else {
            setIsLoading(false);
            alert('Unable to initialize Google Sign-In. Please refresh the page.');
          }
        } catch (error) {
          setIsLoading(false);
          console.error('Error manually rendering Google button:', error);
          alert('Unable to initialize Google Sign-In. Please refresh the page.');
        }
      }
    } else {
      alert('Google Sign-In is not ready. Please refresh the page.');
    }
  };

  return (
    <div className="w-full relative">
      {/* Custom styled button */}
      <button
        onClick={handleClick}
        disabled={disabled || isLoading}
        className="w-full py-3.5 px-6 rounded-xl font-semibold transition-all duration-200 bg-white text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-3 border border-gray-300"
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            <span>Opening sign-in...</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {text}
          </>
        )}
      </button>
      {/* Hidden Google button container - we trigger it programmatically */}
      <div 
        ref={buttonRef} 
        style={{ 
          position: 'absolute', 
          left: '-9999px', 
          top: '-9999px',
          opacity: 0, 
          pointerEvents: 'none',
          width: '100%'
        }}
      ></div>
    </div>
  );
}

export default GoogleSignIn;
